/**
 * Jain Lexicon user directory — staff JWT + tenant.
 * `GET/POST {VITE_API_BASE_URL}/jain_lexicon/users/`
 * `PATCH/DELETE {VITE_API_BASE_URL}/jain_lexicon/users/{id}/`
 */
import { getStaffAccessToken } from "./authApi";
import { formatApiErrorBody } from "./formatApiError";
import { JAIN_LEXICON_GROUP } from "@jl/lib/jainLexiconRbac";

export const LEXICON_USERS_PATH = "/jain_lexicon/users/";

export type LexiconTableRole = "admin" | "scholar" | "contributor";

/** Roles admins may create or fully manage in User Management. */
export type ManageableLexiconRole = "admin" | "scholar" | "contributor";

export type LexiconUserWritePayload = {
  email: string;
  first_name: string;
  last_name: string;
  role: ManageableLexiconRole;
  password?: string;
  password_confirm?: string;
  is_active?: boolean;
};

export type LexiconDirectoryUser = {
  id: string;
  user_id: string;
  email: string | null;
  display_name: string | null;
  is_active: boolean;
  created_at: string;
  contribution_count: number;
  lexicon_role: LexiconTableRole;
};

function jlApiBase(): string {
  const raw =
    typeof import.meta !== "undefined"
      ? (import.meta.env.VITE_API_BASE_URL as string | undefined)
      : undefined;
  return (raw ?? "").replace(/\/$/, "");
}

function jlTenantHeaders(): Record<string, string> {
  const id =
    typeof import.meta !== "undefined"
      ? (import.meta.env.VITE_X_TENANTID as string | undefined)
      : undefined;
  const trimmed = (id ?? "").replace(/^\uFEFF/, "").trim();
  if (!trimmed) {
    return {};
  }
  return { "X-Tenant-Id": trimmed };
}

function coerceList(raw: unknown): Record<string, unknown>[] {
  if (Array.isArray(raw)) {
    return raw.filter(
      (x): x is Record<string, unknown> =>
        typeof x === "object" && x !== null && !Array.isArray(x)
    );
  }
  if (!raw || typeof raw !== "object") {
    return [];
  }
  const o = raw as Record<string, unknown>;
  for (const key of ["results", "data", "content"] as const) {
    const arr = o[key];
    if (Array.isArray(arr)) {
      return arr.filter(
        (x): x is Record<string, unknown> =>
          typeof x === "object" && x !== null && !Array.isArray(x)
      );
    }
  }
  return [];
}

function listNextPageUrl(apiBase: string, raw: unknown): string | null {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) {
    return null;
  }
  const root = raw as Record<string, unknown>;
  let next: unknown = root.next;
  if (next == null && root.links && typeof root.links === "object") {
    next = (root.links as Record<string, unknown>).next;
  }
  if (next === null || next === undefined || typeof next !== "string") {
    return null;
  }
  const trimmed = next.trim();
  if (!trimmed || trimmed.toLowerCase() === "null") {
    return null;
  }
  if (/^https?:\/\//i.test(trimmed)) {
    return trimmed;
  }
  const originMatch = /^(https?:\/\/[^/]+)/i.exec(apiBase);
  const origin = originMatch?.[1];
  const path = trimmed.startsWith("/") ? trimmed : `/${trimmed}`;
  if (origin) {
    return `${origin}${path}`;
  }
  return `${apiBase.replace(/\/$/, "")}${path}`;
}

async function parseHttpError(res: Response): Promise<string> {
  const fallback = `Request failed (${res.status})`;
  try {
    const t = await res.text();
    const trimmed = t.trim();
    if (!trimmed) {
      return fallback;
    }
    try {
      const j = JSON.parse(trimmed) as unknown;
      return formatApiErrorBody(j) ?? fallback;
    } catch {
      return trimmed.length > 300 ? `${trimmed.slice(0, 300)}…` : trimmed;
    }
  } catch {
    return fallback;
  }
}

function pickStr(r: Record<string, unknown>, ...keys: string[]): string | null {
  for (const k of keys) {
    const v = r[k];
    if (v == null) {
      continue;
    }
    const s = String(v).trim();
    if (s.length) {
      return s;
    }
  }
  return null;
}

function groupNamesLowerFromRow(r: Record<string, unknown>): string[] {
  const names: string[] = [];
  const groups = r.groups;
  if (Array.isArray(groups)) {
    for (const item of groups) {
      if (item && typeof item === "object" && !Array.isArray(item)) {
        const n = (item as Record<string, unknown>).name;
        if (typeof n === "string" && n.trim()) {
          names.push(n.toLowerCase());
        }
      }
    }
  }
  const single =
    pickStr(r, "permission_group", "lexicon_group", "group_name") ?? "";
  if (single.trim()) {
    names.push(single.toLowerCase());
  }
  const singleObj = r.group;
  if (
    singleObj &&
    typeof singleObj === "object" &&
    !Array.isArray(singleObj)
  ) {
    const n = (singleObj as Record<string, unknown>).name;
    if (typeof n === "string" && n.trim()) {
      names.push(n.toLowerCase());
    }
  }
  return names;
}

function resolveLexiconRole(r: Record<string, unknown>): LexiconTableRole {
  if (isLexiconAdminRow(r)) {
    return "admin";
  }

  const apiRole = (pickStr(r, "role") ?? "").toLowerCase();
  if (apiRole === "tenant_scholar") {
    return "scholar";
  }
  if (apiRole === "tenant_contributor") {
    return "contributor";
  }

  const names = groupNamesLowerFromRow(r);

  for (const g of names) {
    if (g.includes("scholar")) {
      return "scholar";
    }
  }

  const appRole = (
    pickStr(r, "app_role", "portal_role", "lexicon_role", "account_type") ??
    ""
  ).toLowerCase();
  if (appRole === "admin") {
    return "admin";
  }
  if (appRole === "scholar") {
    return "scholar";
  }
  if (appRole === "contributor") {
    return "contributor";
  }

  for (const g of names) {
    if (
      g.includes("contributer") ||
      g.includes("contributor") ||
      g.includes("jain_lexicon_contributer")
    ) {
      return "contributor";
    }
  }

  if (names.length > 0) {
    return "contributor";
  }

  const panelRole = (pickStr(r, "panel_role") ?? "").toLowerCase();
  if (panelRole === "admin") {
    return "admin";
  }
  if (panelRole === "scholar") {
    return "scholar";
  }
  if (panelRole === "contributor") {
    return "contributor";
  }

  return "contributor";
}

function isLexiconAdminRow(r: Record<string, unknown>): boolean {
  const apiRole = (pickStr(r, "role") ?? "").toLowerCase();
  if (apiRole === "tenant_admin") {
    return true;
  }

  for (const g of groupNamesLowerFromRow(r)) {
    if (g.includes("jain_lexicon_admin") || g === JAIN_LEXICON_GROUP.admin) {
      return true;
    }
  }

  const app = (pickStr(r, "app_role", "portal_role") ?? "").toLowerCase();
  if (app === "admin") {
    return true;
  }

  return false;
}

function coerceDirectoryUserActive(raw: unknown): boolean {
  if (raw === false || raw === "false" || raw === 0 || raw === "0") {
    return false;
  }
  if (
    typeof raw === "string" &&
    raw.trim().toLowerCase() === "inactive"
  ) {
    return false;
  }
  return true;
}

function mapRow(raw: Record<string, unknown>): LexiconDirectoryUser | null {
  const r =
    raw.user && typeof raw.user === "object" && !Array.isArray(raw.user)
      ? ({ ...(raw.user as Record<string, unknown>), ...raw } as Record<
          string,
          unknown
        >)
      : raw;

  const userId =
    pickStr(r, "user_id", "id") ?? pickStr(r, "uuid", "pk") ?? "";
  if (!userId.trim()) {
    return null;
  }

  const id = pickStr(r, "id") ?? userId;

  const email = pickStr(r, "email");
  const displayName =
    pickStr(r, "full_name", "display_name") ||
    [
      pickStr(r, "first_name") ?? "",
      pickStr(r, "last_name") ?? "",
    ]
      .join(" ")
      .trim() ||
    pickStr(r, "tenant_username") ||
    (email ? email.split("@")[0] : null);

  const isActive = coerceDirectoryUserActive(r.is_active ?? r.active);

  const createdRaw = pickStr(
    r,
    "created_on",
    "created_at",
    "date_joined",
    "joined_at"
  );
  const created =
    createdRaw ?? pickStr(r, "updated_on") ?? new Date().toISOString();

  let contributionCount = 0;
  const cc = r.contribution_count ?? r.contributions_count ?? r.term_count;
  if (typeof cc === "number" && Number.isFinite(cc)) {
    contributionCount = cc;
  }

  return {
    id,
    user_id: userId,
    email: email ?? null,
    display_name: displayName,
    is_active: isActive,
    created_at: created,
    contribution_count: contributionCount,
    lexicon_role: resolveLexiconRole(r),
  };
}

export async function fetchLexiconDirectoryUsers(): Promise<{
  users: LexiconDirectoryUser[];
  error: string | null;
}> {
  const base = jlApiBase();
  const token = getStaffAccessToken();
  if (!base) {
    return { users: [], error: "Missing VITE_API_BASE_URL" };
  }
  if (!token) {
    return { users: [], error: "Sign in with a staff account to load users." };
  }

  const headers: Record<string, string> = {
    Accept: "application/json",
    ...jlTenantHeaders(),
    Authorization: `Bearer ${token}`,
  };

  const merged: LexiconDirectoryUser[] = [];
  const seen = new Set<string>();
  let url: string | null = `${base}${LEXICON_USERS_PATH}`;
  const maxPages = 200;
  let pages = 0;

  while (url && pages < maxPages) {
    const res = await fetch(url, { method: "GET", headers });
    if (!res.ok) {
      return {
        users: merged.length > 0 ? merged : [],
        error: await parseHttpError(res),
      };
    }

    let raw: unknown;
    try {
      raw = await res.json();
    } catch {
      return {
        users: merged.length > 0 ? merged : [],
        error: "Invalid JSON from users API",
      };
    }

    const list = coerceList(raw);
    for (const rec of list) {
      const row = mapRow(rec);
      if (!row) {
        continue;
      }
      const key = row.user_id || row.id;
      if (seen.has(key)) {
        continue;
      }
      seen.add(key);
      merged.push(row);
    }

    url = listNextPageUrl(base, raw);
    pages += 1;
  }

  return { users: merged, error: null };
}

function staffAuthHeaders(json = false): Record<string, string> {
  const token = getStaffAccessToken();
  const headers: Record<string, string> = {
    Accept: "application/json",
    ...jlTenantHeaders(),
  };
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }
  if (json) {
    headers["Content-Type"] = "application/json";
  }
  return headers;
}

function staffAuthGuard(): { base: string; headers: Record<string, string> } | {
  error: string;
} {
  const base = jlApiBase();
  const token = getStaffAccessToken();
  if (!base) {
    return { error: "Missing VITE_API_BASE_URL" };
  }
  if (!token) {
    return { error: "Sign in with a staff account to manage users." };
  }
  return { base, headers: staffAuthHeaders() };
}

export function isManageableLexiconUser(
  user: LexiconDirectoryUser
): user is LexiconDirectoryUser & { lexicon_role: ManageableLexiconRole } {
  return (
    user.lexicon_role === "admin" ||
    user.lexicon_role === "scholar" ||
    user.lexicon_role === "contributor"
  );
}

export function apiRoleForManageableRole(role: ManageableLexiconRole): string {
  if (role === "admin") return "tenant_admin";
  if (role === "scholar") return "tenant_scholar";
  return "tenant_contributor";
}

function jlTenantBodyFields(): Record<string, unknown> {
  const id =
    typeof import.meta !== "undefined"
      ? (import.meta.env.VITE_X_TENANTID as string | undefined)
      : undefined;
  const trimmed = (id ?? "").replace(/^\uFEFF/, "").trim();
  if (!trimmed) {
    return {};
  }
  return { tenant_id: trimmed };
}

/** Bodies tried in order until POST succeeds (backend variants differ). */
function buildUserCreateBodyVariants(
  payload: LexiconUserWritePayload
): Record<string, unknown>[] {
  const email = payload.email.trim();
  const first_name = payload.first_name.trim();
  const last_name = payload.last_name.trim();
  const full_name = [first_name, last_name].filter(Boolean).join(" ");
  const password = payload.password?.trim() ?? "";
  const password_confirm = (payload.password_confirm ?? password).trim();
  const is_active = payload.is_active ?? true;
  const tenantRole = apiRoleForManageableRole(payload.role);
  const appRole = payload.role;
  const tenantFields = jlTenantBodyFields();

  const base = {
    ...tenantFields,
    email,
    first_name,
    last_name,
    full_name,
    password,
    password_confirm,
    is_active,
  };

  return [
    // Primary: matches documented curl format exactly
    {
      ...base,
      app_role: appRole,
      lexicon_role: appRole,
      signup_role: appRole,
    },
    // Fallback variants for older backend versions
    { ...base, role: tenantRole, app_role: appRole, account_type: appRole },
    { ...base, role: tenantRole },
    { ...base, role: appRole, account_type: appRole },
    { ...base, role: appRole },
  ];
}

function buildUserUpdateBody(
  payload: Partial<LexiconUserWritePayload>
): Record<string, unknown> {
  const body: Record<string, unknown> = { ...jlTenantBodyFields() };
  if (payload.email !== undefined) {
    body.email = payload.email.trim();
  }
  if (payload.first_name !== undefined) {
    body.first_name = payload.first_name.trim();
  }
  if (payload.last_name !== undefined) {
    body.last_name = payload.last_name.trim();
  }
  if (payload.role !== undefined) {
    body.role = apiRoleForManageableRole(payload.role);
    body.account_type = payload.role;
    body.app_role = payload.role;
    body.lexicon_role = payload.role;
    body.signup_role = payload.role;
  }
  if (payload.is_active !== undefined) {
    body.is_active = payload.is_active;
  }
  if (payload.password?.trim()) {
    const pw = payload.password.trim();
    body.password = pw;
    body.password_confirm = (payload.password_confirm ?? pw).trim();
  }
  return body;
}

async function requestLexiconUserByIds(
  method: "PATCH" | "DELETE",
  ids: string[],
  body?: Record<string, unknown>
): Promise<{ ok: boolean; error: string | null; data?: Record<string, unknown> }> {
  const auth = staffAuthGuard();
  if ("error" in auth) {
    return { ok: false, error: auth.error };
  }
  const { base, headers } = auth;
  let lastErr: string | null =
    method === "DELETE" ? "Could not delete user." : "Could not update user.";

  for (const id of ids) {
    const url = `${base}${LEXICON_USERS_PATH}${encodeURIComponent(id)}/`;
    const res = await fetch(url, {
      method,
      headers: body ? staffAuthHeaders(true) : headers,
      body: body ? JSON.stringify(body) : undefined,
    });
    if (res.ok || res.status === 204) {
      if (method === "DELETE" || res.status === 204) {
        return { ok: true, error: null };
      }
      try {
        const raw = (await res.json()) as unknown;
        if (raw && typeof raw === "object" && !Array.isArray(raw)) {
          return { ok: true, error: null, data: raw as Record<string, unknown> };
        }
      } catch {
        return { ok: true, error: null };
      }
      return { ok: true, error: null };
    }
    lastErr = await parseHttpError(res);
  }

  return { ok: false, error: lastErr };
}

function rowIds(row: Pick<LexiconDirectoryUser, "id" | "user_id">): string[] {
  return [...new Set([row.id, row.user_id].filter(Boolean))];
}

/** POST — admin creates scholar or contributor (staff JWT + tenant). */
export async function createLexiconDirectoryUser(
  payload: LexiconUserWritePayload
): Promise<{
  user: LexiconDirectoryUser | null;
  error: string | null;
}> {
  const auth = staffAuthGuard();
  if ("error" in auth) {
    return { user: null, error: auth.error };
  }
  const { base } = auth;
  const url = `${base}${LEXICON_USERS_PATH}`;
  const variants = buildUserCreateBodyVariants({
    ...payload,
    is_active: payload.is_active ?? true,
  });

  let lastError: string | null = "Could not create user.";
  for (const body of variants) {
    let res: Response;
    try {
      res = await fetch(url, {
        method: "POST",
        headers: staffAuthHeaders(true),
        body: JSON.stringify(body),
      });
    } catch (e) {
      const msg = e instanceof Error ? e.message : "Network error";
      return { user: null, error: msg };
    }

    if (res.ok) {
      try {
        const raw = await res.json();
        const row =
          raw && typeof raw === "object" && !Array.isArray(raw)
            ? mapRow(raw as Record<string, unknown>)
            : null;
        return { user: row, error: null };
      } catch {
        return { user: null, error: null };
      }
    }

    lastError = await parseHttpError(res);
    if (res.status >= 400 && res.status < 500) {
      return { user: null, error: lastError };
    }
  }

  return {
    user: null,
    error:
      lastError ??
      "Server error while creating user. Confirm the API accepts POST /jain_lexicon/users/ with email, password, password_confirm, first_name, last_name, and role.",
  };
}

/** PATCH — update user profile, role, password, or active flag. */
export async function updateLexiconDirectoryUser(
  row: LexiconDirectoryUser,
  payload: Partial<LexiconUserWritePayload>
): Promise<{ user: LexiconDirectoryUser | null; error: string | null }> {

  const body = buildUserUpdateBody(payload);

  const { ok, error, data } = await requestLexiconUserByIds("PATCH", rowIds(row), body);
  if (!ok) {
    return { user: null, error };
  }
  const mapped = data ? mapRow(data) : null;
  return { user: mapped ?? { ...row, ...payload } as LexiconDirectoryUser, error: null };
}

/** DELETE — remove a user directory row. */
export async function deleteLexiconDirectoryUser(
  row: LexiconDirectoryUser
): Promise<{ ok: boolean; error: string | null }> {
  return requestLexiconUserByIds("DELETE", rowIds(row));
}

/** PATCH directory row `is_active` (staff JWT + tenant). Tries `row.id` then `row.user_id` if the first URL fails. */
export async function updateLexiconDirectoryUserActive(
  row: LexiconDirectoryUser,
  isActive: boolean
): Promise<{ ok: boolean; error: string | null }> {
  const { ok, error } = await requestLexiconUserByIds("PATCH", rowIds(row), {
    is_active: isActive,
  });
  return { ok, error };
}

export function normalizeLexiconEmail(email: string): string {
  return email.trim().toLowerCase();
}

/** True when another directory row already uses this email (case-insensitive). */
export function isLexiconEmailTaken(
  email: string,
  users: Iterable<LexiconDirectoryUser>,
  exclude?: { user_id?: string | null; id?: string | null }
): boolean {
  const norm = normalizeLexiconEmail(email);
  if (!norm || !norm.includes("@")) {
    return false;
  }
  const excludeKeys = new Set<string>();
  if (exclude?.user_id?.trim()) {
    excludeKeys.add(exclude.user_id.trim().toLowerCase());
  }
  if (exclude?.id?.trim()) {
    excludeKeys.add(exclude.id.trim().toLowerCase());
  }

  for (const row of users) {
    const rowKeys = [
      row.user_id?.trim().toLowerCase(),
      row.id?.trim().toLowerCase(),
    ].filter(Boolean) as string[];
    if (rowKeys.some(k => excludeKeys.has(k))) {
      continue;
    }
    const rowEmail = row.email ? normalizeLexiconEmail(row.email) : "";
    if (rowEmail && rowEmail === norm) {
      return true;
    }
  }
  return false;
}

/** Reloads directory from API, then checks email is not already taken. */
export async function assertLexiconEmailAvailable(
  email: string,
  exclude?: { user_id?: string | null; id?: string | null }
): Promise<{ available: boolean; error: string | null }> {
  const { users, error } = await fetchLexiconDirectoryUsers();
  if (error) {
    return { available: false, error };
  }
  if (isLexiconEmailTaken(email, users, exclude)) {
    return { available: false, error: null };
  }
  return { available: true, error: null };
}

/**
 * Same request this page uses (for Postman / docs). Token is not exposed.
 */
export function getLexiconUsersRequestSpec(): {
  method: "GET";
  url: string;
  headers: Record<string, string>;
} {
  const base = jlApiBase();
  const url = base ? `${base}${LEXICON_USERS_PATH}` : LEXICON_USERS_PATH;
  const tid =
    typeof import.meta !== "undefined"
      ? (import.meta.env.VITE_X_TENANTID as string | undefined)
      : undefined;
  const tenant = (tid ?? "").replace(/^\uFEFF/, "").trim();
  return {
    method: "GET",
    url,
    headers: {
      Authorization: "Bearer <staff_access_token>",
      Accept: "application/json",
      "X-Tenant-Id": tenant || "(not set — VITE_X_TENANTID)",
    },
  };
}
